import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const { locale } = useI18n();
  const navigate = useNavigate();

  const t = (tr, en) => (locale === "en" ? en : tr);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(t("Giriş başarılı", "Signed in"));
      navigate(u.onboarded ? "/panel" : "/karsilama");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Giriş başarısız", "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="group" data-testid="login-brand">
            <Wordmark size="sm" />
          </Link>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition" data-testid="login-back-home">
            <ArrowLeft size={12} />
            {t("Ana sayfaya dön", "Back to home")}
          </Link>
        </div>
      </header>

      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-sm acas-fade-up" data-testid="login-card">
          <div className="acas-eyebrow mb-2">{t("Giriş", "Sign in")}</div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">
            {t("Tekrar hoş geldiniz", "Welcome back")}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {t("Hesabınıza giriş yapın", "Sign in to your account")}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("E-posta", "Email")}</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@company.com" className="mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("Parola", "Password")}</Label>
                <Link to="/sifremi-unuttum" className="text-xs text-primary hover:underline" data-testid="forgot-link">
                  {t("Şifremi unuttum", "Forgot password?")}
                </Link>
              </div>
              <Input
                id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="••••••••" className="mt-1.5"
              />
            </div>

            <Button type="submit" disabled={busy} className="w-full gap-2 h-10" data-testid="login-submit">
              {busy ? t("Giriş yapılıyor…", "Signing in…") : t("Giriş Yap", "Sign in")}
              <ArrowRight size={16} />
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border/60 text-sm text-muted-foreground text-center">
            {t("Hesabınız yok mu?", "No account yet?")}{" "}
            <Link to="/kayit" className="text-primary font-semibold hover:underline" data-testid="register-link">
              {t("Kayıt olun", "Sign up")}
            </Link>
          </div>

          <div className="mt-8 text-[10px] uppercase tracking-[0.22em] text-muted-foreground text-center" data-testid="login-tagline">
            AI Business Development Employee
          </div>
        </div>
      </div>
    </div>
  );
}
